import { logUsage } from 'owa-analytics';
import { getGuid } from 'owa-guid';
import {
    lazyLogCachedContentRendered,
    lazyLogClientLayout,
    lazyLogResultsRendered,
    ResultsView,
} from 'owa-search-instrumentation';
import SubstrateSearchScenario from 'owa-search-service/lib/data/schema/SubstrateSearchScenario';
import ItemId from 'owa-service/lib/contract/ItemId';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import getMRUAttachmentStore from '../store/mruAttachmentStore';
import { ReplyWithAttachmentSuggestion } from '../store/schema/AttachmentSuggestions';
import { KEY_NULLREFERENCEITEMID } from './constants';

export function logAttachMenuData(
    attachmentSuggestions: ReplyWithAttachmentSuggestion[],
    referenceItemId: ItemId,
    defaultButtonsList: string,
    logicalId: string,
    newLogicalId: string,
    isMail: boolean
) {
    const menuData = getAttachMenuData(attachmentSuggestions);

    // Log data now.
    // This datapoint is used in OWA intelligence scorecard.
    // Make sure any change won't break the scorecard
    logUsage('AttachButtonMenuOpened', [...menuData, defaultButtonsList, true /* isMail */], {
        isCore: true,
        isCosmosDatapoint: true,
    });

    const traceId = getAttachmentSuggestionsTraceId(referenceItemId);
    if (!!traceId) {
        const sessionSettings = getUserConfiguration().SessionSettings;
        lazyLogCachedContentRendered.importAndExecute(
            SubstrateSearchScenario.SuggestedAttachments,
            sessionSettings.UserPuid,
            sessionSettings.ExternalDirectoryTenantGuid,
            logicalId,
            newLogicalId,
            getGuid() /* conversationId */,
            traceId
        );

        logAttachmentSuggestionsClientLayout(attachmentSuggestions, traceId, newLogicalId);
    }
}

export function logAttachMenuDataOnClose(
    attachmentSuggestions: ReplyWithAttachmentSuggestion[],
    isMail: boolean
) {
    const menuData = getAttachMenuData(attachmentSuggestions);

    // Log data now.
    // This datapoint is used in OWA intelligence scorecard.
    // Make sure any change won't break the scorecard
    logUsage('AttachButtonMenuClosed', [true /* isMail */, ...menuData], {
        isCore: true,
        isCosmosDatapoint: true,
    });
}

function getAttachMenuData(attachmentSuggestions: ReplyWithAttachmentSuggestion[]) {
    const endpoints: number[] = [];
    const ids: string[] = [];
    const ranks: number[] = [];
    const provenances: string[] = [];
    const fileTypes: string[] = [];

    if (attachmentSuggestions) {
        attachmentSuggestions.forEach((item, index) => {
            endpoints.push(item.Endpoint);
            ids.push(item.Id);
            ranks.push(index);
            provenances.push(item.FileSourceType);
            fileTypes.push(item.FileExtension);
        });
    }

    return [
        endpoints.join(','),
        ids.join(','),
        ranks.join(','),
        provenances.join(','),
        fileTypes.join(','),
    ];
}

export function getAttachmentSuggestions(
    referenceItemId: ItemId | null
): ReplyWithAttachmentSuggestion[] {
    return (
        (referenceItemId?.Id
            ? getMRUAttachmentStore().contextualAttachmentSuggestions.get(referenceItemId?.Id)
                  ?.attachments
            : getMRUAttachmentStore().attachments) || []
    );
}

export function getAttachmentSuggestionsTraceId(referenceItemId: ItemId | null): string | null {
    return (
        (referenceItemId?.Id
            ? getMRUAttachmentStore().contextualAttachmentSuggestions.get(referenceItemId?.Id)
                  ?.traceId
            : getMRUAttachmentStore().traceId) || null
    );
}

export function logAttachmentSuggestionsClientLayout(
    attachmentSuggestions: ReplyWithAttachmentSuggestion[],
    traceId: string,
    logicalId: string
) {
    const resultsView: ResultsView[] = [];
    attachmentSuggestions.forEach((item, index) => {
        resultsView.push({
            type: 'Entity',
            name: 'Files',
            referenceId: item?.ReferenceId,
            providerId: item?.TraceId,
            position: index + 1, // position starts from 1
            entityType: 'File',
            placementType: 'Visible',
        });
    });

    const sessionSettings = getUserConfiguration().SessionSettings;
    lazyLogClientLayout.importAndExecute(
        SubstrateSearchScenario.SuggestedAttachments,
        sessionSettings.UserPuid,
        sessionSettings.ExternalDirectoryTenantGuid,
        logicalId,
        traceId,
        'Vertical',
        resultsView
    );
}

export function logAttachmentMenuSuggestionData(
    attachmentSuggestions: ReplyWithAttachmentSuggestion[],
    suggestedAttachmentsLogicalId: string,
    suggestedAttachmentsTraceId: string,
    clearSuggestedAttachmentsTraceId: () => void,
    attachMenuOpenTime: number,
    referenceItemId: ItemId
) {
    const currentTraceId =
        attachmentSuggestions[0]?.TraceId || getAttachmentSuggestionsTraceId(referenceItemId);
    if (!!suggestedAttachmentsTraceId && currentTraceId !== suggestedAttachmentsTraceId) {
        lazyLogResultsRendered.importAndExecute(
            SubstrateSearchScenario.SuggestedAttachments,
            suggestedAttachmentsLogicalId,
            currentTraceId,
            new Date().getTime() - attachMenuOpenTime
        );
        logAttachmentSuggestionsClientLayout(
            attachmentSuggestions,
            currentTraceId,
            suggestedAttachmentsLogicalId
        );
        clearSuggestedAttachmentsTraceId();
    }
}

export function getAttachmentSuggestionsTraceIdByComposeId(
    composeId: string | null
): string | null {
    const idMap = getMRUAttachmentStore().referenceItemIdAndComposeIdMap;
    for (let referenceItemIdString of idMap.keys()) {
        if (idMap.get(referenceItemIdString).some(c => c === composeId)) {
            return getAttachmentSuggestionsTraceId(
                referenceItemIdString === KEY_NULLREFERENCEITEMID
                    ? null
                    : { Id: referenceItemIdString }
            );
        }
    }

    return null;
}
